// deviceAlarmsAdapter — single isolation point for the per-device alarms endpoint
// (sun-contract-proposal.md §10.2, issue sun#3519).
//
// Sun contract: GET monitoring/deviceAlarms/{deviceId}
//   auth: Apikey <token>, authority MONITORING || MOS.
// Served on demand when the machine detail opens (like deviceProduction). Returns a
// bare array already sorted by severity (ERR → WAR → INF) and, within each severity,
// most recent first:
//   [{ id, code, description, level, startedAtMillis }]
// A device with no active alarms returns `200 []` (not an error). The alarm DURATION
// is derived in the front from `startedAtMillis`. Acknowledge (§10.3) does not exist
// yet: the panel is read-only for now (phase 2).
//
// On any failure (network error, or the endpoint not existing yet) it returns a
// degraded result with an empty list instead of throwing, so callers never need their
// own try/catch and simply render the empty state.
import { apiGet } from '../generic/Api_Functions'

const DEVICE_ALARMS_ENDPOINT = 'monitoring/deviceAlarms'

const EMPTY_DEVICE_ALARMS = { deviceId: 0, alarms: [], degraded: false }

/**
 * @param {number|string} deviceId
 * @returns {Promise<{deviceId: number|string, alarms: object[], degraded: boolean}>}
 *   `alarms` is sun's already-sorted list (ERR→WAR→INF, newest first) or `[]` when the
 *   device has no active alarms; `degraded` is true only on a transport/shape failure.
 */
async function fetchDeviceAlarms (deviceId) {
  try {
    const response = await apiGet(DEVICE_ALARMS_ENDPOINT + '/' + deviceId)
    return {
      deviceId,
      alarms: Array.isArray(response) ? response : [],
      degraded: false
    }
  } catch (error) {
    return { ...EMPTY_DEVICE_ALARMS, deviceId, degraded: true }
  }
}

export { DEVICE_ALARMS_ENDPOINT, EMPTY_DEVICE_ALARMS, fetchDeviceAlarms }
